// Utilities to keep iOS WKWebView from leaving the page panned/scrollable (especially after
// portaled overlays like Radix Popover + native permission prompts).

export function fixNativeIosViewportAfterOverlay() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  // iOS WKWebView can sometimes end up in a "zoomed" state after native permission prompts.
  // We can't programmatically pinch-zoom, but we *can* force the viewport meta back to a
  // known scale and prevent further scaling in native iOS.
  try {
    let meta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "viewport";
      document.head?.appendChild(meta);
    }

    // Keep this minimal: enforce scale=1 and viewport-fit for safe areas.
    // Setting maximum-scale=1 prevents getting stuck zoomed-in again.
    meta.setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover"
    );
  } catch {
    // ignore
  }

  // Extra defensive: ensure we never keep a horizontal scrollable area.
  try {
    document.documentElement.style.overflowX = "hidden";
    document.body.style.overflowX = "hidden";
    // Also prevent iOS from applying "smart" text scaling after overlays.
    // (Some WebViews ignore the CSS property intermittently.)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (document.documentElement.style as any).webkitTextSizeAdjust = "100%";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (document.body.style as any).webkitTextSizeAdjust = "100%";
  } catch {
    // ignore
  }

  const scrollingEl = (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
  const top = scrollingEl.scrollTop ?? window.scrollY ?? 0;

  const reset = () => {
    try {
      // Reset horizontal pan while preserving vertical position.
      scrollingEl.scrollLeft = 0;
      scrollingEl.scrollTo({ left: 0, top });
    } catch {
      try {
        window.scrollTo({ left: 0, top });
      } catch {
        // ignore
      }
    }
  };

  // Run multiple times because iOS can apply scroll adjustments after layout/keyboard updates.
  reset();
  requestAnimationFrame(reset);
  window.setTimeout(reset, 50);
}
